// ---- primitives ----

/** Trim a string (kept as a tiny helper for readability). */
export const trim = (value: string): string => value.trim();

/**
 * Convert a string to `undefined` if, after trimming, it's empty.
 * Useful for PATCH payloads where "empty" means "omit the field".
 */
export const emptyToUndefined = (value: string): string | undefined => {
  const v = value.trim();
  return v === '' ? undefined : v;
};

/**
 * Convert a string to `null` if, after trimming, it's empty.
 * Useful when your API expects explicit nulls to clear values.
 */
export const emptyToNull = (value: string): string | null => {
  const v = value.trim();
  return v === '' ? null : v;
};

/**
 * Convert a value (string enum / number) to `undefined` if it is the empty placeholder "".
 * Intended for select controls where "" means "not selected".
 */
export const valueToUndefined = <T extends string | number>(value: T | ''): T | undefined =>
  value === '' ? undefined : value;

/**
 * Convert a value (string enum / number) to `null` if it is the empty placeholder "".
 */
export const valueToNull = <T extends string | number>(value: T | ''): T | null =>
  value === '' ? null : value;

/**
 * For <select> elements holding numeric IDs.
 * DOM provides strings, so this converts to number or "" for the placeholder.
 */
export const parseNumberOrEmpty = (raw: string): number | '' => (raw === '' ? '' : Number(raw));

// ---- object helpers ----

/**
 * Remove keys whose value is `undefined`.
 * Keeps `null` (because null is sometimes meaningful to APIs).
 */
export const omitUndefined = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v !== undefined)) as Partial<T>;
};

/**
 * Remove keys whose value is `null` or `undefined`.
 * Use this ONLY if your API treats null the same as "not provided".
 */
export const omitNullish = <T extends Record<string, any>>(obj: T): Partial<T> => {
  return Object.fromEntries(Object.entries(obj).filter(([, v]) => v != null)) as Partial<T>;
};

/**
 * Build an object from an input and a mapping spec.
 * Each spec function receives the whole input and returns the value for that key.
 */
export const mapObject = <TIn, TOut extends Record<string, any>>(
  input: TIn,
  spec: { [K in keyof TOut]: (src: TIn) => TOut[K] }
): TOut => {
  const out: any = {};
  for (const key in spec) out[key] = spec[key](input);
  return out as TOut;
};

/**
 * Builds a stable, fully-populated defaults object for a form.
 *
 * Why:
 * - Keeps defaulting logic central and consistent
 * - Avoids repeating `initial?.x ?? ""` all over the place
 * - Produces a predictable value shape for React Hook Form
 *
 * Rules:
 * - `base` should define *all* keys (the full form shape).
 * - `initial` overrides are applied shallowly (top-level only).
 * - If you need deep merge, do it explicitly per-form.
 */
export function buildDefaults<T extends Record<string, unknown>>(base: T, initial?: Partial<T>): T {
  if (!initial) return base;

  // Shallow merge is intentional for forms: nested objects should be explicit.
  return { ...base, ...initial };
}

import type { z } from 'zod';

/**
 * Factory for building a standard "form module" object.
 *
 * Architectural intent:
 * - Standardises how forms expose schema/defaults/mapper
 * - Makes it easy to scaffold new forms and keep naming consistent
 * - Keeps "defaults live outside schemas" and "API semantics live in mappers"
 *
 * Notes:
 * - This helper is intentionally thin. It's not trying to be a framework.
 * - It mainly provides consistent structure + type inference.
 */
export function createFormModule<
  TSchema extends z.ZodTypeAny,
  TInitial extends Record<string, unknown>,
  TDto extends Record<string, unknown>,
>(args: {
  /** Zod schema for UI-facing form values */
  schema: TSchema;

  /**
   * Base defaults for the form (must include every key in Values).
   * Used with `buildDefaults` in the `defaults()` function.
   */
  baseDefaults: z.infer<TSchema>;

  /**
   * Default builder: turns partial initial values into full form values.
   * Usually implemented via `buildDefaults(baseDefaults, initial)`.
   */
  defaults: (initial?: Partial<TInitial>) => z.infer<TSchema>;

  /**
   * Mapper: converts validated form values into API payload DTO.
   * This is where "empty string → undefined" etc happens.
   */
  mapper: (values: z.infer<TSchema>) => TDto;
}) {
  return args;
}
